"use client";

import { useCallback, useState, useSyncExternalStore } from "react";
import { useTranslations } from "next-intl";
import { formatRegionalTime } from "@/lib/i18n/config/languages";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/use-translation";
import { CopyButton } from "../components/debug-primitives";
import {
  clearApiLog,
  getApiLogEntries,
  subscribeApiLog,
} from "../stores/api-log-store";

function statusColor(status: number | null): string {
  if (status === null) return "text-muted-foreground";
  if (status === 0) return "text-red-500";
  if (status >= 500) return "text-red-500";
  if (status >= 400) return "text-orange-500";
  if (status >= 300) return "text-blue-400";
  return "text-emerald-500";
}

function methodColor(method: string): string {
  const colors: Record<string, string> = {
    GET: "text-blue-500",
    POST: "text-emerald-500",
    PUT: "text-amber-500",
    PATCH: "text-amber-400",
    DELETE: "text-red-500",
  };
  return colors[method] ?? "text-muted-foreground";
}

export function ApiLogPanel() {
  const t = useTranslations("debug.apiLog");
  const { formatLocale, timeZone } = useTranslation("common");
  const entries = useSyncExternalStore(
    subscribeApiLog,
    getApiLogEntries,
    getApiLogEntries,
  );
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"response" | "request">(
    "response",
  );

  const handleClear = useCallback(() => {
    clearApiLog();
    setExpandedId(null);
  }, []);

  const toggleExpand = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  if (entries.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-4 text-center">
        {t("empty")}
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-muted-foreground">
          {t("entryCount", { count: entries.length })}
        </span>
        <button
          onClick={handleClear}
          className="text-sm px-2 py-0.5 rounded border border-border bg-muted/50 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
        >
          {t("clear")}
        </button>
      </div>
      <div className="space-y-1">
        {entries.map((entry) => {
          const isExpanded = expandedId === entry.id;
          const hasDetails = Boolean(entry.requestBody || entry.responseBody);

          return (
            <div
              key={entry.id}
              className={cn(
                "rounded border transition-colors overflow-hidden",
                isExpanded
                  ? "border-border bg-muted/20"
                  : "border-transparent hover:bg-muted/30",
              )}
            >
              <div
                onClick={() => toggleExpand(entry.id)}
                className="flex items-center gap-2 py-1 px-2 text-sm font-mono cursor-pointer select-none group"
              >
                <span className="text-muted-foreground/60 w-16 shrink-0 text-sm">
                  {formatRegionalTime(entry.timestamp, formatLocale, timeZone)}
                </span>
                <span
                  className={cn(
                    "w-12 shrink-0 font-semibold",
                    methodColor(entry.method),
                  )}
                >
                  {entry.method}
                </span>
                <span
                  className={cn(
                    "w-10 shrink-0 font-semibold text-center",
                    statusColor(entry.status),
                  )}
                >
                  {entry.status === null
                    ? "…"
                    : entry.status === 0
                      ? t("errorStatus")
                      : entry.status}
                </span>
                <span className="flex-1 truncate text-foreground/80">
                  {entry.url}
                </span>
                <span className="text-muted-foreground/60 w-14 shrink-0 text-right">
                  {entry.duration !== null ? `${entry.duration}ms` : ""}
                </span>
                {hasDetails && (
                  <span className="shrink-0 font-sans text-sm text-muted-foreground/50">
                    {isExpanded ? "▼" : "▶"}
                  </span>
                )}
                {entry.error && (
                  <span
                    className="text-red-400 truncate max-w-[120px]"
                    title={entry.error}
                  >
                    {entry.error}
                  </span>
                )}
              </div>

              {isExpanded && (
                <div className="space-y-2 border-t border-border/50 bg-background/50 p-3 font-mono text-sm">
                  {/* Tab Selector */}
                  <div className="flex items-center justify-between border-b border-border/40 pb-1.5">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setActiveTab("response")}
                        className={cn(
                          "px-2 py-0.5 rounded text-sm transition-colors cursor-pointer",
                          activeTab === "response"
                            ? "bg-primary/10 text-primary font-semibold border border-primary/20"
                            : "text-muted-foreground hover:text-foreground",
                        )}
                      >
                        {t("responseData")}
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveTab("request")}
                        className={cn(
                          "px-2 py-0.5 rounded text-sm transition-colors cursor-pointer",
                          activeTab === "request"
                            ? "bg-primary/10 text-primary font-semibold border border-primary/20"
                            : "text-muted-foreground hover:text-foreground",
                        )}
                      >
                        {t("requestPayload")}
                      </button>
                    </div>

                    {activeTab === "response" && entry.responseBody && (
                      <CopyButton text={entry.responseBody} />
                    )}
                    {activeTab === "request" && entry.requestBody && (
                      <CopyButton text={entry.requestBody} />
                    )}
                  </div>

                  {/* Body Content */}
                  {activeTab === "response" && (
                    <pre className="p-2.5 rounded bg-muted/40 border border-border/40 max-h-60 overflow-y-auto overflow-x-auto text-foreground/90 whitespace-pre-wrap break-all select-text">
                      {entry.responseBody || t("noResponseBody")}
                    </pre>
                  )}

                  {activeTab === "request" && (
                    <pre className="p-2.5 rounded bg-muted/40 border border-border/40 max-h-60 overflow-y-auto overflow-x-auto text-foreground/90 whitespace-pre-wrap break-all select-text">
                      {entry.requestBody || t("noRequestBody")}
                    </pre>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
